// v3.0.0-rc.8t — LiveLandingPage : la page qui s'ouvre quand un viewer scanne le QR
// Inspiré de la capture 2 (overlay BOUTIQUE + produit épinglé + achat en 1 clic).
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

function fmtFcfa(n) { return Number(n || 0).toLocaleString("fr-FR"); }

export default function LiveLandingPage() {
  const { merchantId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qrToken = params.get("s");
  // v3.0.0-rc.8w — Discovery mode : arrivée depuis le Halo AYA avec session_id direct (pas de qr_token).
  const discoverSessionId = params.get("session");

  const [merchant, setMerchant] = useState(null);
  const [pinnedProduct, setPinnedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);

  const track = useCallback(async (sessionId) => {
    try {
      await fetch(`${API}/v3/live/sessions/${sessionId}/scan`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source: qrToken ? "qr" : "aya_halo_discovery", token: qrToken }),
      });
    } catch (_) {}
  }, [qrToken]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const mr = await fetch(`${API}/v3/merchants/${merchantId}`);
        const md = mr.ok ? await mr.json() : null;
        if (!md?.merchant) throw new Error("merchant_not_found");
        setMerchant(md.merchant);
        let sessionData = null;
        if (discoverSessionId) {
          // Discovery mode : résolution directe via session_id (public endpoint).
          const rr = await fetch(`${API}/v3/live/sessions/${encodeURIComponent(discoverSessionId)}`);
          if (rr.ok) { const rb = await rr.json(); sessionData = rb.session; }
        } else {
          // Legacy QR mode : résolution via merchant + qr_token.
          const rr = await fetch(`${API}/v3/live/resolve?merchant=${encodeURIComponent(merchantId)}&token=${encodeURIComponent(qrToken || "")}`);
          if (rr.ok) { const rb = await rr.json(); sessionData = rb.session; }
        }
        if (sessionData) {
          setSession(sessionData);
          track(sessionData.session_id);
          if (sessionData.pinned_product_id) {
            const pinned = (md.products || []).find((p) => (p.id || p.product_id) === sessionData.pinned_product_id);
            setPinnedProduct(pinned || null);
          }
        }
      } catch (e) { setError("Boutique introuvable."); }
      finally { setLoading(false); }
    })();
  }, [merchantId, qrToken, discoverSessionId, track]);

  if (loading) return <div style={_wrap}><div style={_center}>Chargement…</div></div>;
  if (error || !merchant) return (
    <div style={_wrap}><div style={_center}>{error || "Boutique introuvable."}<br/><button onClick={() => navigate("/")} style={_btnGhost}>← Retour AYA</button></div></div>
  );

  return (
    <div style={_wrap} data-testid="live-landing-root">
      {/* Bandeau overlay style capture 2 */}
      <div style={_bannerAya} data-testid="live-landing-banner">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: -1, color: "#EBD9A8" }}>AYA</div>
          <div>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.7 }}>Boutique</div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{merchant.name} <span style={{ color: "#10B981" }}>✓</span></div>
            {merchant.category && <div style={{ fontSize: 11, opacity: 0.7 }}>{merchant.category}</div>}
          </div>
        </div>
        {session && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 11 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#EF4444", boxShadow: "0 0 0 4px rgba(239,68,68,0.25)" }} />
            <strong>Pendant le LIVE</strong>
          </div>
        )}
        <div style={_trustRow}>
          <span>🛡 Paiement sécurisé</span>
          <span>🛵 Livraison rapide</span>
          <span>✓ Commerçant vérifié</span>
        </div>
      </div>

      {/* v3.0.0-rc.8y — P0 : Player Live intégré. Regarde TikTok/IG/FB/YT sans quitter AYA. */}
      {session && (session.live_url || session.platform_hint) && (
        <LivePlayerEmbed
          liveUrl={session.live_url}
          platformHint={session.platform_hint}
          merchantName={merchant.name}
        />
      )}

      {/* Produit épinglé — le vendeur montre CE produit en direct */}
      {pinnedProduct && (
        <div style={{ ..._card, background: "linear-gradient(135deg, #E4C87F, #CDA452)", color: "#3A2E10" }} data-testid="live-landing-pinned">
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 800 }}>🎁 EN LIVE MAINTENANT</div>
          <h2 style={{ margin: "6px 0 4px", fontSize: 18, fontWeight: 900 }}>{pinnedProduct.name}</h2>
          <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: -0.5 }}>{fmtFcfa(pinnedProduct.price_fcfa)} FCFA</div>
          <button
            data-testid="live-landing-buy-pinned"
            onClick={() => navigate(`/boutique/${merchant.merchant_id}?product=${pinnedProduct.id || pinnedProduct.product_id}&live=${session?.session_id}`)}
            style={{ marginTop: 10, padding: "12px 20px", borderRadius: 999, background: "#0B4531", color: "#EBD9A8", border: "none", fontWeight: 800, fontSize: 14, cursor: "pointer", width: "100%" }}
          >Acheter maintenant →</button>
        </div>
      )}

      <button
        data-testid="live-landing-shop"
        onClick={() => navigate(`/boutique/${merchant.merchant_id}?live=${session?.session_id || ''}`)}
        style={{ ..._btnPrimary, width: "100%", marginTop: 12 }}
      >Voir toute la boutique →</button>
    </div>
  );
}

const _wrap = { maxWidth: 480, margin: "0 auto", padding: "16px", color: "#F4F1E8", minHeight: "100vh", background: "radial-gradient(circle at top, rgba(11,69,49,0.5), transparent 60%), #02120A" };
const _bannerAya = { background: "linear-gradient(135deg, #0B0F2E, #060820)", border: "2px solid rgba(232,213,163,0.5)", borderRadius: 16, padding: 16, marginBottom: 14, position: "relative", overflow: "hidden" };
const _trustRow = { display: "flex", flexWrap: "wrap", gap: 10, marginTop: 10, fontSize: 10, opacity: 0.75 };
const _card = { border: "1px solid rgba(232,213,163,0.35)", borderRadius: 16, padding: 16, marginBottom: 12 };
const _btnPrimary = { padding: "14px 20px", borderRadius: 999, background: "linear-gradient(135deg, #F5E9C6, #E4C87F 55%, #CDA452)", color: "#3A2E10", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer" };
const _btnGhost = { background: "transparent", color: "#EBD9A8", border: "1px solid rgba(232,213,163,0.35)", borderRadius: 999, padding: "8px 16px", fontSize: 12, cursor: "pointer", marginTop: 10 };
const _center = { textAlign: "center", padding: 30 };

// v3.0.0-rc.8y — P0 Player Live intégré : parse l'URL/plateforme et embed iframe si possible,
// sinon fallback élégant avec CTA "Ouvrir sur <plateforme>". Autoplay muted pour respecter
// les policies mobiles (Chrome/Safari bloquent autoplay avec son).
// NOTE : TikTok, Instagram et Snap bloquent explicitement les iframes sur domaines tiers
// ("Embed blocked"). On sert un fallback CTA dès qu'on détecte ces plateformes.
function parseLiveUrl(url, platformHint) {
  if (!url && !platformHint) return { platform: null };
  const u = url || "";
  // TikTok — jamais d'embed direct (TikTok bloque tout iframe non-officiel).
  let m = u.match(/tiktok\.com\/@([^/?#]+)/i);
  if (m) return { platform: "tiktok", username: m[1], embed_url: null, orientation: "portrait" };
  // Instagram — pas d'embed live officiel accessible.
  m = u.match(/instagram\.com\/([^/?#]+)/i);
  if (m) return { platform: "instagram", username: m[1], embed_url: null, orientation: "portrait" };
  // Snap — pas d'embed possible.
  if (/snapchat\.com|snap\.com/i.test(u)) return { platform: "snap", embed_url: null, orientation: "portrait" };
  // Facebook Live — video plugin fonctionne pour la plupart des vidéos publiques.
  if (/facebook\.com|fb\.watch/i.test(u)) {
    return { platform: "facebook", embed_url: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(u)}&show_text=false&width=400&height=560&autoplay=true`, orientation: "portrait" };
  }
  // YouTube (watch?v= | live/ | embed/ | youtu.be) — embed toujours OK.
  m = u.match(/(?:youtube\.com\/(?:watch\?v=|live\/|embed\/)|youtu\.be\/)([\w-]{6,})/i);
  if (m) return { platform: "youtube", video_id: m[1], embed_url: `https://www.youtube.com/embed/${m[1]}?autoplay=1&mute=1`, orientation: "landscape" };
  // Fallback : plateforme connue via hint mais URL non parseable → CTA seulement.
  return { platform: platformHint || null, embed_url: null, orientation: "portrait" };
}

function LivePlayerEmbed({ liveUrl, platformHint, merchantName }) {
  const parsed = parseLiveUrl(liveUrl, platformHint);
  const [loaded, setLoaded] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const platformMeta = {
    tiktok: { label: "TikTok", color: "#000", accent: "#FF0050", icon: "🎵" },
    instagram: { label: "Instagram", color: "#833AB4", accent: "#E1306C", icon: "📸" },
    facebook: { label: "Facebook", color: "#1877F2", accent: "#1877F2", icon: "📘" },
    youtube: { label: "YouTube", color: "#FF0000", accent: "#FF0000", icon: "▶" },
    snap: { label: "Snap", color: "#FFFC00", accent: "#000", icon: "👻" },
  };
  const meta = platformMeta[parsed.platform] || { label: "Plateforme", color: "#666", accent: "#999", icon: "🌐" };
  const canEmbed = !!parsed.embed_url && !failed;
  const aspect = parsed.orientation === "landscape" ? "16 / 9" : "9 / 16";

  return (
    <div data-testid="live-player-embed" style={{ marginBottom: 14, position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, padding: "0 4px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#EF4444", boxShadow: "0 0 0 4px rgba(239,68,68,0.25)", animation: "aya-live-pulse 1.6s ease-in-out infinite" }} />
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase" }}>Live · {meta.icon} {meta.label}</span>
        </div>
        {liveUrl && (
          <a
            href={liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="live-player-open-native"
            style={{ fontSize: 10, opacity: 0.7, color: "#EBD9A8", textDecoration: "underline" }}
          >Ouvrir dans l&apos;app native ↗</a>
        )}
      </div>
      {canEmbed ? (
        <div style={{ position: "relative", width: "100%", aspectRatio: aspect, background: "#000", borderRadius: 16, overflow: "hidden", border: "1px solid rgba(232,213,163,0.28)", boxShadow: "0 6px 24px rgba(0,0,0,0.55)" }}>
          {!loaded && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.6)", fontSize: 12 }}>
              Connexion au live…
            </div>
          )}
          <iframe
            data-testid="live-player-iframe"
            title={`Live ${meta.label} ${merchantName || ""}`}
            src={parsed.embed_url}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen; accelerometer; gyroscope"
            allowFullScreen
            sandbox="allow-scripts allow-same-origin allow-popups allow-presentation allow-forms"
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
          />
        </div>
      ) : (
        <FallbackWatchCTA parsed={parsed} liveUrl={liveUrl} meta={meta} merchantName={merchantName} />
      )}
      <style>{`@keyframes aya-live-pulse { 0%,100% { transform:scale(1); opacity:1;} 50% { transform:scale(1.25); opacity:0.55; } }`}</style>
    </div>
  );
}

function FallbackWatchCTA({ parsed, liveUrl, meta, merchantName }) {
  const openUrl = liveUrl || (parsed?.username ? `https://www.${parsed.platform}.com/${parsed.platform === "tiktok" ? "@" : ""}${parsed.username}` : "#");
  return (
    <div
      data-testid="live-player-fallback"
      style={{
        aspectRatio: "9 / 16",
        borderRadius: 16, overflow: "hidden",
        background: `linear-gradient(135deg, ${meta.color}, ${meta.accent}), #000`,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: 20, textAlign: "center", position: "relative",
        border: "1px solid rgba(232,213,163,0.28)",
      }}
    >
      <div style={{ fontSize: 56, marginBottom: 12, filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.4))" }}>{meta.icon}</div>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", opacity: 0.85, marginBottom: 4 }}>Live sur {meta.label}</div>
      <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 4 }}>{merchantName || "Commerçant"}</div>
      {parsed?.username && <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 16 }}>@{parsed.username}</div>}
      <div style={{ fontSize: 11, opacity: 0.85, maxWidth: 260, lineHeight: 1.5, marginBottom: 16 }}>
        {meta.label} bloque la lecture directe dans les apps tierces. Ouvre la vidéo dans l&apos;app native pour regarder — reviens ici pour acheter le produit épinglé 🎁
      </div>
      <a
        href={openUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="live-player-fallback-cta"
        style={{
          padding: "12px 24px", borderRadius: 999,
          background: "#fff", color: meta.color, fontWeight: 800, fontSize: 13,
          textDecoration: "none", boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
        }}
      >{meta.icon} Regarder sur {meta.label} ↗</a>
    </div>
  );
}
